import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useApi } from '../../../hooks/ApiProvider/ApiProvider';
import styles from './OrderEdit.module.css';

interface OrderInfo {
  id: string;
  customer_id: string;
  customer_name?: string;
  product_id: string;
  order_name?: string;
  payment_state: string;
  payment_key?: string;
  amount?: number;
  is_gift?: boolean;
  phone_number?: string;
  created_at: string;
  updated_at?: string;
}

const OrderEdit: React.FC = () => {
  const { orderId } = useParams<{ orderId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { getOrderDetail, updateOrder } = useApi();
  
  const [order, setOrder] = useState<OrderInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  
  // 폼 상태
  const [formData, setFormData] = useState({
    payment_state: '',
    customer_name: '',
    phone_number: '',
    is_gift: false
  });

  useEffect(() => {
    const fetchOrderDetail = async () => {
      if (!orderId) return;

      try {
        setLoading(true);
        setError(null);

        const orderData = await getOrderDetail(orderId);
        setOrder(orderData);
        
        // 폼 데이터 초기화
        setFormData({
          payment_state: orderData.payment_state,
          customer_name: orderData.customer_name || '',
          phone_number: orderData.phone_number || '',
          is_gift: orderData.is_gift || false
        });
      } catch (err: any) {
        setError(err.message || '주문 정보를 불러오는데 실패했습니다.');
      } finally {
        setLoading(false);
      }
    };

    fetchOrderDetail();
  }, [orderId, getOrderDetail]);

  // 날짜 포맷팅
  const formatDate = (dateString: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // 금액 포맷팅
  const formatAmount = (amount?: number) => {
    if (amount === undefined || amount === null) return '-';
    return new Intl.NumberFormat('ko-KR').format(amount) + '원';
  };

  // 결제 상태 옵션
  const paymentStateOptions = [
    { value: 'ISSUED', label: '대기 (ISSUED)', icon: '⏳' },
    { value: 'PENDING', label: '대기 (PENDING)', icon: '⏳' },
    { value: 'PAID', label: '완료 (PAID)', icon: '✅' },
    { value: 'CANCELLED', label: '취소 (CANCELLED)', icon: '❌' },
    { value: 'REFUNDED', label: '환불 (REFUNDED)', icon: '↩️' },
    { value: 'FAILED', label: '실패 (FAILED)', icon: '⚠️' },
    { value: 'REJECTED', label: '거부 (REJECTED)', icon: '🚫' }
  ];

  // 폼 입력 처리
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value
    }));
  };

  // 폼 제출 처리
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!order) return;

    try {
      setSaving(true);
      
      // 현재는 결제 상태만 수정 가능
      const updateData = {
        payment_state: formData.payment_state,
        // customer_name: formData.customer_name, // 향후 주문자명 수정 기능 추가 예정
        // phone_number: formData.phone_number, // 향후 전화번호 수정 기능 추가 예정
        // is_gift: formData.is_gift // 향후 선물여부 수정 기능 추가 예정
      };

      const response = await updateOrder(order.id, updateData);
      
      // API 응답이 성공적으로 반환되면 성공으로 처리
      if (response) {
        alert('✅ 결제 상태가 성공적으로 업데이트되었습니다.');
        handleGoBack();
      } else {
        alert('❌ 결제 상태 업데이트에 실패했습니다.');
      }
    } catch (error: any) {
      alert(`❌ 업데이트 중 오류가 발생했습니다: ${error.message}`);
    } finally {
      setSaving(false);
    }
  };

  // 뒤로가기 처리
  const handleGoBack = () => {
    const state = location.state;
    if (state && state.from) {
      navigate(state.from);
    } else {
      navigate(`/admin/orders/detail/${orderId}`);
    }
  };

  // 취소 처리
  const handleCancel = () => {
    if (window.confirm('변경사항이 저장되지 않습니다. 정말로 취소하시겠습니까?')) {
      handleGoBack();
    }
  };

  if (loading) {
    return (
      <div className={styles.orderEdit}>
        <div className={styles.pageTitleRow}>
          <h1>결제 정보 수정</h1>
        </div>
        <div className={styles.loadingSpinner}>
          <div style={{ fontSize: '24px', marginBottom: '16px' }}>⏳</div>
          로딩 중...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.orderEdit}>
        <div className={styles.pageTitleRow}>
          <h1>결제 정보 수정</h1>
        </div>
        <div className={styles.errorMessage}>
          <div style={{ fontSize: '24px', marginBottom: '16px' }}>❌</div>
          {error}
          <button onClick={handleGoBack} className={styles.btnBack}>
            ← 뒤로가기
          </button>
        </div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className={styles.orderEdit}>
        <div className={styles.pageTitleRow}>
          <h1>결제 정보 수정</h1>
        </div>
        <div className={styles.errorMessage}>
          <div style={{ fontSize: '24px', marginBottom: '16px' }}>🔍</div>
          주문 정보를 찾을 수 없습니다.
          <button onClick={handleGoBack} className={styles.btnBack}>
            ← 뒤로가기
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.orderEdit}>
      <div className={styles.pageTitleRow}>
        <h1>결제 정보 수정</h1>
        <button onClick={handleCancel} className={styles.btnCancel}>
          ✕ 취소
        </button>
      </div>
      
      <div className={styles.orderEditContent}>
        {/* 현재 주문 정보 표시 */}
        <div className={styles.currentInfoSection}>
          <h2>📋 현재 주문 정보</h2>
          <div className={styles.infoGrid}>
            <div className={styles.infoItem}>
              <label>주문 ID</label>
              <span>{order.id}</span>
            </div>
            <div className={styles.infoItem}>
              <label>상품 ID</label>
              <span>{order.product_id}</span>
            </div>
            <div className={styles.infoItem}>
              <label>주문명</label>
              <span>{order.order_name || '-'}</span>
            </div>
            <div className={styles.infoItem}>
              <label>결제 키</label>
              <span>{order.payment_key || '-'}</span>
            </div>
            <div className={styles.infoItem}>
              <label>주문 금액</label>
              <span style={{ fontWeight: '600', color: '#059669' }}>{formatAmount(order.amount)}</span>
            </div>
            <div className={styles.infoItem}>
              <label>주문일</label>
              <span>{formatDate(order.created_at)}</span>
            </div>
            <div className={styles.infoItem}>
              <label>수정일</label>
              <span>{order.updated_at ? formatDate(order.updated_at) : '-'}</span>
            </div>
          </div>
        </div>

        {/* 수정 폼 */}
        <div className={styles.editFormSection}>
          <h2>✏️ 결제 상태 수정</h2>
          <form onSubmit={handleSubmit} className={styles.editForm}>
            <div className={styles.formGrid}>
              <div className={styles.formItem}>
                <label htmlFor="payment_state">결제 상태 *</label>
                <select
                  id="payment_state"
                  name="payment_state"
                  value={formData.payment_state}
                  onChange={handleInputChange}
                  required
                  className={styles.formSelect}
                >
                  <option value="">결제 상태를 선택하세요</option>
                  {paymentStateOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.icon} {option.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* 향후 주문자명 수정 기능 추가 예정
              <div className={styles.formItem}>
                <label htmlFor="customer_name">주문자명</label>
                <input
                  type="text"
                  id="customer_name"
                  name="customer_name"
                  value={formData.customer_name}
                  onChange={handleInputChange}
                  placeholder="주문자명을 입력하세요"
                  className={styles.formInput}
                />
              </div>
              */}

              {/* 향후 전화번호 수정 기능 추가 예정
              <div className={styles.formItem}>
                <label htmlFor="phone_number">전화번호</label>
                <input
                  type="tel"
                  id="phone_number"
                  name="phone_number"
                  value={formData.phone_number}
                  onChange={handleInputChange}
                  placeholder="전화번호를 입력하세요 (예: 010-1234-5678)"
                  className={styles.formInput}
                />
              </div>
              */}

              {/* 향후 선물여부 수정 기능 추가 예정
              <div className={styles.formItem}>
                <label className={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    name="is_gift"
                    checked={formData.is_gift}
                    onChange={handleInputChange}
                    className={styles.formCheckbox}
                  />
                  <span className={styles.checkboxText}>선물 주문</span>
                </label>
              </div>
              */}
            </div>

            <div className={styles.formActions}>
              <button
                type="button"
                onClick={handleCancel}
                className={styles.btnCancel}
                disabled={saving}
              >
                ✕ 취소
              </button>
              <button
                type="submit"
                className={styles.btnSave}
                disabled={saving}
              >
                {saving ? '💾 저장 중...' : '💾 저장'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default OrderEdit;
